import {
  CButton,
  CCol,
  CContainer,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
  CRow,
} from "@coreui/react";
import { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useSelector } from "react-redux";
import { createCatalogItemsBulk } from "../../api/catalog-items";
import CatalogItem, { CatalogSuggestion } from "../../models/catalog-item";
import { RootState } from "../../store";
import { SUCCESS } from "../../utils/constants/tags";
import { errorAlert, successAlert } from "../utils/messages";

const DISMISSED_KEY = "catalogItemNudgeDismissed";

/**
 * Which offers this person already turned down, per company.
 *
 * Kept in the browser rather than in the database because it is a preference
 * about being interrupted and not a fact about the company. Reading it is
 * guarded: a browser with storage blocked has to invoice anyway, and the worst
 * case of losing it is being asked once more.
 */
export const dismissedCatalogNudges = (taxPayerId?: number): string[] => {
  if (taxPayerId === undefined) {
    return [];
  }

  try {
    const raw = window.localStorage.getItem(`${DISMISSED_KEY}:${taxPayerId}`);

    return raw === null ? [] : (JSON.parse(raw) as string[]);
  } catch {
    return [];
  }
};

export const dismissCatalogNudges = (
  taxPayerId: number | undefined,
  normalizedDescriptions: string[],
) => {
  if (taxPayerId === undefined) {
    return;
  }

  try {
    const merged = Array.from(
      new Set([...dismissedCatalogNudges(taxPayerId), ...normalizedDescriptions]),
    );

    window.localStorage.setItem(
      `${DISMISSED_KEY}:${taxPayerId}`,
      JSON.stringify(merged),
    );
  } catch {
    // Nothing to do: not remembering the refusal only costs one more offer.
  }
};

interface CatalogItemNudgeModalProps {
  show: boolean;
  suggestions: CatalogSuggestion[];
  /** Called once the person is done, whatever they chose. */
  onClose: () => void | Promise<void>;
}

/**
 * Offers to catalogue a line that was just invoiced and matched nothing.
 *
 * Appears after the document is issued, never before, and it blocks nothing:
 * the invoice is already out. The offer only comes up from the third time the
 * same description was invoiced, because interrupting over a one-off is how a
 * useful prompt turns into one people dismiss without reading — and then it
 * also gets dismissed the times it was right.
 */
const CatalogItemNudgeModal: React.FC<CatalogItemNudgeModalProps> = ({
  show,
  suggestions,
  onClose,
}) => {
  const taxPayer = useSelector(
    (state: RootState) => state.taxPayer.data.taxPayer,
  );

  const [submitting, setSubmitting] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);

  useEffect(() => {
    setSelected(
      suggestions.map((suggestion) => suggestion.normalizedDescription),
    );
  }, [suggestions]);

  const onToggle = (suggestion: CatalogSuggestion) => {
    setSelected((previous) =>
      previous.indexOf(suggestion.normalizedDescription) >= 0
        ? previous.filter((value) => value !== suggestion.normalizedDescription)
        : [...previous, suggestion.normalizedDescription],
    );
  };

  const onDismiss = () => {
    dismissCatalogNudges(
      taxPayer.id,
      suggestions.map((suggestion) => suggestion.normalizedDescription),
    );

    onClose();
  };

  const onSubmit = async () => {
    const chosen = suggestions.filter(
      (suggestion) => selected.indexOf(suggestion.normalizedDescription) >= 0,
    );

    if (chosen.length === 0) {
      onDismiss();
      return;
    }

    setSubmitting(true);

    const items: CatalogItem[] = chosen.map((suggestion) => ({
      description: suggestion.description,
      code: suggestion.code,
      measureUnit: suggestion.measureUnit,
      ivaType: suggestion.ivaType,
    }));

    const status = await createCatalogItemsBulk(taxPayer.id!, items);

    setSubmitting(false);

    if (status.status !== SUCCESS) {
      errorAlert(status.detail ?? "No se pudo agregar al catálogo.");
      onClose();
      return;
    }

    const linked = status.data?.linked ?? 0;

    successAlert(
      linked > 0
        ? `Agregado. Se vincularon ${linked} líneas ya facturadas.`
        : "Agregado a tus productos y servicios.",
    );

    // What was not ticked was a refusal, and it should not come back.
    dismissCatalogNudges(
      taxPayer.id,
      suggestions
        .filter(
          (suggestion) =>
            selected.indexOf(suggestion.normalizedDescription) < 0,
        )
        .map((suggestion) => suggestion.normalizedDescription),
    );

    onClose();
  };

  return (
    <CModal show={show} onClose={onDismiss} size="lg">
      <CModalHeader closeButton>
        <CModalTitle>¿Lo agregás a tus productos y servicios?</CModalTitle>
      </CModalHeader>
      <CModalBody>
        <CContainer fluid>
          <CRow>
            <CCol sm="12">
              <p>
                La factura ya se emitió. Estas descripciones las venís
                facturando seguido y todavía no están en tu catálogo: si las
                agregás, la próxima vez se completan solas el código, el IVA y
                la unidad.
              </p>
            </CCol>
          </CRow>
          <CRow>
            <CCol sm="12">
              {suggestions.map((suggestion) => (
                <div
                  className="d-flex align-items-center mb-2"
                  key={suggestion.normalizedDescription}
                >
                  <input
                    type="checkbox"
                    className="mr-2"
                    checked={
                      selected.indexOf(suggestion.normalizedDescription) >= 0
                    }
                    onChange={() => onToggle(suggestion)}
                  />
                  <span>
                    «{suggestion.description}»{" "}
                    <span className="text-muted">
                      — {suggestion.items} líneas facturadas
                    </span>
                  </span>
                </div>
              ))}
            </CCol>
          </CRow>
        </CContainer>
      </CModalBody>
      <CModalFooter>
        <CButton color="secondary" disabled={submitting} onClick={onDismiss}>
          No, gracias
        </CButton>
        <CButton color="primary" disabled={submitting} onClick={onSubmit}>
          {submitting ? (
            <Spinner
              animation="grow"
              style={{
                height: "17px",
                width: "17px",
                marginTop: "auto",
                marginBottom: "auto",
                marginRight: "10px",
              }}
            />
          ) : (
            <></>
          )}
          {submitting ? "Agregando..." : "Agregar"}
        </CButton>
      </CModalFooter>
    </CModal>
  );
};

export default CatalogItemNudgeModal;
